import { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import { Instagram, Mail } from "lucide-react";
import { api } from "@/lib/api";

const DEFAULT_CONFIG = {
  email: "hello@simon-o.com",
  instagram: "simon.o.barberphoto",
};

const useSiteConfig = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/config")
      .then((res) => {
        if (!cancelled) setConfig((prev) => ({ ...prev, ...res.data }));
      })
      .catch((err) => {
        // Non-fatal: keep defaults, surface for debug in dev only
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("Contact: /config unreachable, using defaults", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return config;
};

function ContactLink({ label, value, href, Icon, testId }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className="group flex items-center justify-between border-b border-paper/20 hover:border-paper py-6 transition-colors"
    >
      <div>
        <p className="overline text-paper/50 mb-2">{label}</p>
        <p className="font-display text-2xl sm:text-3xl group-hover:text-accent transition-colors">
          {value}
        </p>
      </div>
      <Icon className="w-6 h-6 text-paper/40 group-hover:text-accent group-hover:translate-x-1 transition-all" />
    </a>
  );
}

function Contact() {
  const { t } = useLang();
  const config = useSiteConfig();
  const [first, ...rest] = t.contact.title.split(" ");
  const restTitle = rest.join(" ") || ".";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-ink text-paper py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-6">
          <p className="overline text-paper/60 mb-4">04 — {t.contact.title}</p>
          <h2 className="display-xl text-[3rem] sm:text-[4.6rem] lg:text-[6rem] text-paper leading-none">
            {first}
            <br />
            <span className="italic font-normal text-paper/80">{restTitle}</span>
          </h2>
          <p className="font-display italic text-lg sm:text-xl text-paper/70 mt-6 max-w-md leading-relaxed">
            {t.contact.lead}
          </p>
        </div>

        <div className="lg:col-span-6 flex flex-col justify-center gap-2">
          <ContactLink
            label={t.contact.emailLabel}
            value={config.email}
            href={`mailto:${config.email}`}
            Icon={Mail}
            testId="contact-email"
          />
          <ContactLink
            label={t.contact.instagramLabel}
            value={`@${config.instagram}`}
            href={`https://instagram.com/${config.instagram}`}
            Icon={Instagram}
            testId="contact-instagram"
          />
        </div>
      </div>
    </section>
  );
}

export default Contact;
